import Phaser from 'phaser';
import { MASK_HEIGHT, MASK_WIDTH, TOOL_RADII, type ToolIndex } from '../config/constants';
import type { MaskPoint, SceneCallbacks } from '../game/types';
import { toMaskPoint } from '../input/coordinates';
import type { CleaningModel } from '../systems/CleaningModel';

const GAME_WIDTH = 600;
const GAME_HEIGHT = 800;
const FILTER_BOUNDS = { left: 30, top: 40, width: 540, height: 720 } as const;
const LAYER_COLORS = [
  [86, 75, 62, 170],
  [145, 105, 54, 145],
  [168, 143, 96, 105],
  [81, 124, 137, 72],
] as const;

export class CleaningScene extends Phaser.Scene {
  private readonly model: CleaningModel;
  private readonly callbacks: SceneCallbacks;
  private readonly layerCanvases: HTMLCanvasElement[] = [];
  private readonly layerTextures: Phaser.Textures.CanvasTexture[] = [];
  private lastPoint: MaskPoint | null = null;

  constructor(model: CleaningModel, callbacks: SceneCallbacks) {
    super('CleaningScene');
    this.model = model;
    this.callbacks = callbacks;
  }

  preload(): void {
    this.load.image('clean-filter', './assets/clean-filter.png');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#f7fbff');
    this.createFilterBase();
    this.createDirtLayers();

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => this.handlePointerDown(pointer));
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => this.handlePointerMove(pointer));
    this.input.on('pointerup', () => {
      this.lastPoint = null;
    });
    this.input.on('gameout', () => {
      this.lastPoint = null;
    });

    this.callbacks.onReady();
  }

  resetVisuals(): void {
    for (let stage = 0; stage < this.layerCanvases.length; stage += 1) {
      this.paintLayer(stage as ToolIndex);
    }
    this.lastPoint = null;
  }

  private createFilterBase(): void {
    const source = this.textures.get('clean-filter').getSourceImage() as HTMLImageElement;
    const cropWidth = Math.floor(source.height * (FILTER_BOUNDS.width / FILTER_BOUNDS.height));
    const cropX = Math.max(0, Math.floor((source.width - cropWidth) / 2));
    const filter = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'clean-filter');
    filter.setCrop(cropX, 0, cropWidth, source.height);
    filter.setDisplaySize(FILTER_BOUNDS.width, FILTER_BOUNDS.height);
  }

  private createDirtLayers(): void {
    for (let stage = 0; stage < 4; stage += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = MASK_WIDTH;
      canvas.height = MASK_HEIGHT;
      this.layerCanvases.push(canvas);
      this.paintLayer(stage as ToolIndex);
      const key = `dirt-layer-${stage}`;
      const texture = this.textures.addCanvas(key, canvas);
      if (!texture) {
        throw new Error(`无法创建污渍纹理：${key}`);
      }
      this.layerTextures.push(texture);
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key)
        .setDisplaySize(FILTER_BOUNDS.width, FILTER_BOUNDS.height);
    }
  }

  private paintLayer(stage: ToolIndex): void {
    const canvas = this.layerCanvases[stage];
    const layer = this.model.layers[stage];
    if (!canvas || !layer) {
      return;
    }
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return;
    }
    const image = context.createImageData(MASK_WIDTH, MASK_HEIGHT);
    const base = LAYER_COLORS[stage];
    for (let y = 0; y < MASK_HEIGHT; y += 1) {
      for (let x = 0; x < MASK_WIDTH; x += 1) {
        if (!layer.isActive(x, y)) {
          continue;
        }
        const offset = (y * MASK_WIDTH + x) * 4;
        const variation = ((x * 13 + y * 7) % 17) - 8;
        image.data[offset] = Math.max(0, Math.min(255, base[0] + variation));
        image.data[offset + 1] = Math.max(0, Math.min(255, base[1] + variation));
        image.data[offset + 2] = Math.max(0, Math.min(255, base[2] + variation));
        image.data[offset + 3] = base[3];
      }
    }
    context.putImageData(image, 0, 0);
    this.layerTextures[stage]?.refresh();
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    const availability = this.model.canClean();
    if (availability !== 'ok') {
      this.callbacks.onUnavailable(availability);
      return;
    }
    const point = this.mapPointer(pointer);
    if (!point) {
      return;
    }
    this.lastPoint = point;
    this.clean(point, point, pointer.x, pointer.y);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!pointer.isDown || !this.lastPoint) {
      return;
    }
    const point = this.mapPointer(pointer);
    if (!point) {
      this.lastPoint = null;
      return;
    }
    this.clean(this.lastPoint, point, pointer.x, pointer.y);
    this.lastPoint = point;
  }

  private clean(from: MaskPoint, to: MaskPoint, screenX: number, screenY: number): void {
    const result = this.model.applySegment(from, to);
    if (!result || result.changed === 0) {
      return;
    }
    const canvas = this.layerCanvases[result.stage];
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }
    context.save();
    context.globalCompositeOperation = 'destination-out';
    const radius = TOOL_RADII[result.stage];
    for (const point of result.points) {
      context.beginPath();
      context.arc(point.x, point.y, radius, 0, Math.PI * 2);
      context.fill();
    }
    context.restore();
    this.layerTextures[result.stage]?.refresh();
    this.emitParticles(screenX, screenY, result.stage);
    this.callbacks.onCleaning(result, screenX, screenY);
    if (result.stageCompleted) {
      this.lastPoint = null;
    }
  }

  private emitParticles(x: number, y: number, stage: ToolIndex): void {
    const colors = [0x8c7967, 0xd7a046, 0xffffff, 0x58b9ef];
    for (let index = 0; index < 3; index += 1) {
      const dot = this.add.circle(
        x + Phaser.Math.Between(-14, 14),
        y + Phaser.Math.Between(-14, 14),
        Phaser.Math.Between(4, 9),
        colors[stage],
        0.85,
      );
      this.tweens.add({
        targets: dot,
        x: dot.x + Phaser.Math.Between(-24, 24),
        y: dot.y - Phaser.Math.Between(24, 48),
        alpha: 0,
        scale: 0.35,
        duration: 420,
        ease: 'Cubic.easeOut',
        onComplete: () => dot.destroy(),
      });
    }
  }

  private mapPointer(pointer: Phaser.Input.Pointer): MaskPoint | null {
    return toMaskPoint(pointer.x, pointer.y, FILTER_BOUNDS, MASK_WIDTH, MASK_HEIGHT);
  }
}

export const GAME_CONFIG_SIZE = { width: GAME_WIDTH, height: GAME_HEIGHT } as const;
